import React, { useEffect, useState } from 'react';
import { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { BanIcon, CalendarIcon, CheckIcon, EditIcon, EyeIcon, EyeOffIcon, FileTextIcon, ImageIcon, LayoutDashboardIcon, LoaderCircleIcon, GripVerticalIcon, LogOutIcon, PackageIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { deleteRemoteGalleryImage, loadRemoteGalleryImages, uploadRemoteGalleryImage } from '../../data/gallery';
import { createBlockedDate, createProduct, deleteBlockedDate, deleteBooking, deleteProduct, getMigrationStatus, getSupabaseErrorMessage, loadBlockedDates, loadBookingsForAdmin, loadProducts, loadSettings, migrateLegacyData, saveSettings, saveProductOrder, updateBookingStatus, updateProduct, uploadProductImage } from '../../data/repository';
import { getActiveSession, isAdminSession, signOut } from '../../data/supabase';
import { downloadBackup } from '../../utils/backup';
import { AdminSettings, BlockedDate, Booking, GalleryImage, MigrationStatus, Product, ProductVariant } from '../../data/types';
type Tab = 'dashboard' | 'bookings' | 'products' | 'gallery' | 'blocked' | 'settings';
const defaultSettings: AdminSettings = {
  termsText: '',
  contactInfo: {
    name: '',
    phone: '',
    email: ''
  },
  emailJs: {
    publicKey: '',
    serviceId: '',
    templateId: '',
    notificationEmail: ''
  }
};
export function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('dashboard');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [blocked, setBlocked] = useState<BlockedDate[]>([]);
  const [gallery, setGallery] = useState<GalleryImage[]>([]);
  const [settings, setSettings] = useState<AdminSettings>(defaultSettings);
  const [migration, setMigration] = useState<MigrationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [blockStatus, setBlockStatus] = useState('');
  const [blockError, setBlockError] = useState('');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [productImages, setProductImages] = useState<string[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [draggedProductId, setDraggedProductId] = useState<string | null>(null);
  const openProductEditor = (product: Product) => {
    setEditingProduct(product);
    setProductImages([product.image, ...(product.images || [])]);
    setProductModalOpen(true);
  };
  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [remoteBookings, remoteProducts, remoteBlocked, remoteGallery, remoteSettings, remoteMigration] = await Promise.all([loadBookingsForAdmin(), loadProducts(), loadBlockedDates(), loadRemoteGalleryImages(), loadSettings(), getMigrationStatus()]);
      setBookings(remoteBookings);
      setProducts(remoteProducts);
      setBlocked(remoteBlocked);
      setGallery(remoteGallery);
      setSettings(remoteSettings);
      setMigration(remoteMigration);
    } catch (loadError) {
      setError(getSupabaseErrorMessage(loadError));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    void getActiveSession().then(session => {
      if (!isAdminSession(session)) navigate('/admin', {
        replace: true
      });else void loadData();
    });
  }, [navigate]);
  const runAction = async (action: () => Promise<void>, message: string) => {
    setIsSaving(true);
    try {
      await action();
      await loadData();
      toast.success(message);
    } catch (actionError) {
      toast.error(getSupabaseErrorMessage(actionError));
    } finally {
      setIsSaving(false);
    }
  };
  const logout = async () => {
    await signOut();
    navigate('/admin', {
      replace: true
    });
  };
  const reorderProducts = async (targetProductId: string) => {
    if (!draggedProductId || draggedProductId === targetProductId) return;
    const fromIndex = products.findIndex(product => product.id === draggedProductId);
    const toIndex = products.findIndex(product => product.id === targetProductId);
    if (fromIndex < 0 || toIndex < 0) return;
    const nextProducts = [...products];
    const [movedProduct] = nextProducts.splice(fromIndex, 1);
    if (!movedProduct) return;
    nextProducts.splice(toIndex, 0, movedProduct);
    const orderedProducts = nextProducts.map((product, index) => ({
      ...product,
      displayOrder: index
    }));
    setProducts(orderedProducts);
    setDraggedProductId(null);
    setIsSaving(true);
    try {
      await saveProductOrder(orderedProducts);
      toast.success('Redoslijed proizvoda je spremljen.');
    } catch (reorderError) {
      setProducts(products);
      toast.error(getSupabaseErrorMessage(reorderError));
    } finally {
      setIsSaving(false);
    }
  };
  const saveProduct = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const name = String(form.get('name') || '').trim();
    const image = productImages[0] || String(form.get('image') || '').trim();
    if (!name || !image) {
      toast.error('Naziv i najmanje jedna slika su obavezni.');
      return;
    }
    const variants = parseVariants(String(form.get('variants') || ''));
    if (variants === null) {
      toast.error('Varijante moraju biti ispravan JSON popis.');
      return;
    }
    const product: Product = {
      id: editingProduct?.id || createId(),
      name,
      description: optionalText(form.get('description')),
      dimensions: optionalText(form.get('dimensions')),
      ageRestriction: optionalText(form.get('ageRestriction')),
      capacity: optionalText(form.get('capacity')),
      usageTerms: optionalText(form.get('usageTerms')),
      application: String(form.get('application')),
      basePrice: Number(form.get('basePrice')),
      deliveryPrice: 0,
      image,
      images: productImages.slice(1),
      category: String(form.get('category')),
      isActive: editingProduct?.isActive !== false,
      displayOrder: editingProduct?.displayOrder ?? products.length,
      variants: variants || undefined
    };
    await runAction(async () => {
      if (editingProduct) await updateProduct(editingProduct.id, product);else await createProduct(product);
      setProductModalOpen(false);
      setEditingProduct(null);
      setProductImages([]);
    }, editingProduct ? 'Proizvod je ažuriran.' : 'Proizvod je dodan.');
  };
  const selectProductImages = async (files: FileList | null) => {
    const items = Array.from(files || []);
    if (!items.length) return;
    if (items.some(file => file.size > 50 * 1024 * 1024)) {
      toast.error('Pojedina slika ne smije biti veća od 50 MB.');
      return;
    }
    setIsUploadingImages(true);
    try {
      const urls = await Promise.all(items.map(uploadProductImage));
      setProductImages(current => [...current, ...urls]);
      toast.success(`Preneseno ${urls.length} slika.`);
    } catch (uploadError) {
      toast.error(getSupabaseErrorMessage(uploadError));
    } finally {
      setIsUploadingImages(false);
    }
  };
  const createBlock = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const startDate = String(form.get('date') || '');
    const endDate = optionalText(form.get('endDate'));
    if (endDate && endDate < startDate) {
      setBlockError('Završni datum ne može biti prije početnog datuma.');
      setBlockStatus('');
      return;
    }
    setBlockStatus('Spremamo blokadu…');
    setBlockError('');
    setIsSaving(true);
    try {
      await createBlockedDate({
        id: createId(),
        date: startDate,
        endDate,
        productId: optionalText(form.get('productId')),
        reason: optionalText(form.get('reason'))
      });
      await loadData();
      formElement.reset();
      setBlockStatus('Termin je uspješno blokiran.');
      toast.success('Termin je blokiran.');
    } catch (blockActionError) {
      const message = getSupabaseErrorMessage(blockActionError);
      setBlockError(message);
      setBlockStatus('');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };
  const saveAllSettings = async (event: FormEvent) => {
    event.preventDefault();
    await runAction(async () => {
      await saveSettings(settings);
    }, 'Postavke su spremljene.');
  };
  const migrate = async () => {
    if (!window.confirm('Kopirati postojeće lokalne podatke u praznu Supabase bazu? Ova se radnja može napraviti samo jednom.')) return;
    await runAction(async () => {
      const result = await migrateLegacyData();
      toast.success(`Preneseno: ${result.products} proizvoda, ${result.bookings} rezervacija i ${result.blockedDates} blokada.`);
    }, 'Prijenos podataka je dovršen.');
  };
  if (isLoading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center gap-3 text-gray-600" data-id="element-187" data-mp="1nn1y3rf6eita">
        <LoaderCircleIcon className="animate-spin" data-id="element-188" data-mp="1ioi9fj976sx8" />
        Učitavamo administratorske podatke…
      </div>;
  const pending = bookings.filter(booking => booking.status === 'pending').length;
  const confirmed = bookings.filter(booking => booking.status === 'confirmed');
  const revenue = confirmed.reduce((sum, booking) => sum + booking.totalPrice, 0);
  return <div className="min-h-screen bg-gray-50 md:flex" data-id="element-189" data-mp="zvo7pr0l4xay">
      <Toaster position="top-center" richColors data-id="element-190" data-mp="2qblpd7lidmzz" />
      <aside className="bg-gray-900 text-white md:w-64" data-id="element-191" data-mp="2qaanxefjcrc1">
        <div className="p-6 border-b border-gray-800" data-id="element-192" data-mp="2lcsbvf681vzt">
          <h1 className="text-xl font-black" data-id="element-193" data-mp="2ld265eyaupbb">JumpLand Admin</h1>
          <p className="mt-1 text-xs text-gray-400" data-id="element-194" data-mp="2gcmpoeiq11p4">Supabase povezano</p>
        </div>
        <nav className="flex gap-1 overflow-x-auto p-3 md:block md:space-y-1" data-id="element-195" data-mp="2beuoetw6s389">
          {([{
          id: 'dashboard',
          label: 'Pregled',
          icon: LayoutDashboardIcon
        }, {
          id: 'bookings',
          label: 'Rezervacije',
          icon: CalendarIcon
        }, {
          id: 'products',
          label: 'Proizvodi',
          icon: PackageIcon
        }, {
          id: 'gallery',
          label: 'Galerija',
          icon: ImageIcon
        }, {
          id: 'blocked',
          label: 'Blokirani termini',
          icon: BanIcon
        }, {
          id: 'settings',
          label: 'Postavke',
          icon: FileTextIcon
        }] as {
          id: Tab;
          label: string;
          icon: typeof PackageIcon;
        }[]).map(item => {
          const Icon = item.icon;
          return <button key={item.id} type="button" onClick={() => setTab(item.id)} className={`flex shrink-0 items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-bold md:w-full ${tab === item.id ? 'bg-primary text-gray-900' : 'text-gray-300 hover:bg-gray-800'}`} data-id="element-196" data-mp="3bo46kx2bdz5g">
                <Icon size={18} data-id="element-197" data-mp="2swjq2tfghh8e" />
                {item.label}
              </button>;
        })}
        </nav>
        <div className="hidden p-4 md:block" data-id="element-198" data-mp="2xu33jr4bxpza">
          <button type="button" onClick={() => void logout()} className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-red-300 hover:bg-red-400/10" data-id="element-199" data-mp="2xss05sesqk8p">
            <LogOutIcon size={18} data-id="element-200" data-mp="2zk8k1qy0808d" />
            Odjava
          </button>
        </div>
      </aside>
      <main className="flex-1 p-5 md:p-8" data-id="element-201" data-mp="2ul5dg22dllya">
        {error && <div role="alert" className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800" data-id="element-202" data-mp="2ulow5kzuoedd">
            {error}
            <button type="button" onClick={() => void loadData()} className="ml-3 font-bold underline" data-id="element-203" data-mp="22ur50ld4t1u0">
              Pokušaj ponovno
            </button>
          </div>}
        {tab === 'dashboard' && <section data-id="element-204" data-mp="3s8xcz2owoz91">
            <h2 className="text-3xl font-black text-gray-900" data-id="element-205" data-mp="3s8e40bh8hl75">
              Nadzorna ploča
            </h2>
            <div className="mt-7 grid gap-5 sm:grid-cols-2 xl:grid-cols-4" data-id="element-206" data-mp="3nam0slb52cnn">
              <Stat label="Ukupno rezervacija" value={bookings.length} data-id="element-207" data-mp="192bfkkgtqjmd" />
              <Stat label="Na čekanju" value={pending} data-id="element-208" data-mp="19462237qujsk" />
              <Stat label="Potvrđeno" value={confirmed.length} data-id="element-209" data-mp="1e27i0f9vi2zt" />
              <Stat label="Vrijednost potvrđenih" value={`${revenue} €`} data-id="element-210" data-mp="101mzzlbs8b7d" />
            </div>
            <div className="mt-8 rounded-3xl border border-gray-100 bg-white p-6" data-id="element-211" data-mp="150gffjamqca6">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-212" data-mp="152kjq2cfxdaz">
                Zajednička pohrana
              </h3>
              <p className="mt-2 max-w-2xl text-gray-600" data-id="element-213" data-mp="1wrmhi59wuk23">
                Proizvodi, rezervacije, blokade, postavke i galerija sada se
                spremaju u Supabase te su dostupni na svim uređajima.
              </p>
            </div>
          </section>}
        {tab === 'bookings' && <section data-id="element-214" data-mp="2bp6jr4ffa14k">
            <h2 className="text-3xl font-black text-gray-900" data-id="element-215" data-mp="2bomzvi67p2nw">Rezervacije</h2>
            <div className="mt-6 overflow-x-auto rounded-3xl border border-gray-100 bg-white" data-id="element-216" data-mp="2gfd6eyqdjbk6">
              <table className="w-full min-w-[760px] text-left" data-id="element-217" data-mp="rtsj2xp1p8me">
                <thead className="bg-gray-50 text-sm text-gray-600" data-id="element-218" data-mp="rv3qr8lm30is">
                  <tr data-id="element-219" data-mp="mto276k86bvn">
                    <th className="p-4" data-id="element-220" data-mp="mtebthv9q2pf">Termin</th>
                    <th className="p-4" data-id="element-221" data-mp="ism44t21o5v9">Korisnik</th>
                    <th className="p-4" data-id="element-222" data-mp="itdw7twfnlgs">Proizvod</th>
                    <th className="p-4" data-id="element-223" data-mp="dusyq28kffw1">Status</th>
                    <th className="p-4 text-right" data-id="element-224" data-mp="dw42w35xapno">Akcije</th>
                  </tr>
                </thead>
                <tbody data-id="element-225" data-mp="1am4ndpz9ejz6">
                  {bookings.map(booking => <tr key={booking.id} className="border-t border-gray-100" data-id="element-226" data-mp="16j8amfwehi35">
                      <td className="p-4 font-bold" data-id="element-227" data-mp="11qyg0mkcbtuj">
                        {booking.date}
                        {booking.endDate ? ` – ${booking.endDate}` : ''}
                        <span className="block text-xs font-normal text-gray-500" data-id="element-228" data-mp="1u810bumfitpb">
                          {booking.startTime} – {booking.endTime}
                        </span>
                      </td>
                      <td className="p-4" data-id="element-229" data-mp="1wt8p2yhv2l0s">
                        <strong data-id="element-230" data-mp="1wua2vaq9hlm7">{booking.customerName}</strong>
                        <span className="block text-xs text-gray-500" data-id="element-231" data-mp="21r0gzsiia74z">
                          {booking.phone}
                        </span>
                        {booking.locationImage && <a href={booking.locationImage} target="_blank" rel="noreferrer" className="mt-2 inline-block" data-id="element-232" data-mp="2asqjpuhu8si2">
                            <img src={booking.locationImage} alt="Privatna slika lokacije" className="h-12 w-12 rounded-lg object-cover" data-id="element-233" data-mp="2kobep8ohosib" />
                          </a>}
                      </td>
                      <td className="p-4" data-id="element-234" data-mp="33ge0eyfzmyzf">
                        {booking.productName}
                        <span className="block text-xs text-gray-500" data-id="element-235" data-mp="13eeg0xa6r1ln">
                          {booking.totalPrice} €
                        </span>
                      </td>
                      <td className="p-4" data-id="element-236" data-mp="1d4pkqm1p1etl">
                        <Status status={booking.status} data-id="element-237" data-mp="1d5r73mo5tnqm" />
                      </td>
                      <td className="p-4 text-right" data-id="element-238" data-mp="18dr2hgx5a6np">
                        <div className="inline-flex gap-2" data-id="element-239" data-mp="18bwgs0e1uaik">
                          {booking.status === 'pending' && <>
                              <IconButton label="Potvrdi" onClick={() => void runAction(() => updateBookingStatus(booking.id, 'confirmed'), 'Rezervacija je potvrđena.')} data-id="element-240" data-mp="1i2r4l96y1i64">
                                <CheckIcon size={17} data-id="element-241" data-mp="2dbuoveg7kvcs" />
                              </IconButton>
                              <IconButton label="Odbij" onClick={() => void runAction(() => updateBookingStatus(booking.id, 'rejected'), 'Rezervacija je odbijena.')} data-id="element-242" data-mp="1zkg71ay1f07b">
                                <XIcon size={17} data-id="element-243" data-mp="3b1poxt1ipk6i" />
                              </IconButton>
                            </>}
                          <IconButton label="Obriši" danger onClick={() => {
                      if (window.confirm('Obrisati rezervaciju?')) void runAction(() => deleteBooking(booking), 'Rezervacija je obrisana.');
                    }} data-id="element-244" data-mp="3ezx3ch8ie9ej">
                            <Trash2Icon size={17} data-id="element-245" data-mp="1f3bp4hvpx5ty" />
                          </IconButton>
                        </div>
                      </td>
                    </tr>)}
                  {bookings.length === 0 && <tr data-id="element-246" data-mp="155xi18vopteb">
                      <td colSpan={5} className="p-10 text-center text-gray-500" data-id="element-247" data-mp="156p7e1youuw5">
                        Nema rezervacija.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </section>}
        {tab === 'products' && <section data-id="element-248" data-mp="3c0gbjwg6qz8x">
            <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-249" data-mp="3bzya8leo1uu5">
              <div data-id="element-250" data-mp="3gtsqtfyz5tp0">
                <h2 className="text-3xl font-black text-gray-900" data-id="element-251" data-mp="3gukl8ngu5ldp">
                  Proizvodi
                </h2>
                <p className="mt-1 text-sm text-gray-600" data-id="element-252" data-mp="3ltf4fqiptbyc">
                  Povucite karticu za promjenu redoslijeda na ponudi.
                </p>
              </div>
              <button type="button" onClick={() => {
            setEditingProduct(null);
            setProductImages([]);
            setProductModalOpen(true);
          }} className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-4 py-3 font-bold text-white" data-id="element-253" data-mp="2t3f321nfah08">
                <PlusIcon size={18} data-id="element-254" data-mp="iac92df1pm9k" />
                Dodaj proizvod
              </button>
            </div>
            <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-id="element-255" data-mp="1u6guxrbctwkh">
              {products.map(product => <article key={product.id} draggable={!isSaving} onDragStart={() => setDraggedProductId(product.id)} onDragOver={event => event.preventDefault()} onDrop={() => void reorderProducts(product.id)} onDragEnd={() => setDraggedProductId(null)} className={`rounded-2xl border bg-white p-4 transition-all ${draggedProductId === product.id ? 'border-primary opacity-50' : 'border-gray-100'}`} data-id="element-256" data-mp="1z3gzfxsk2r9h">
                  <button type="button" onClick={() => openProductEditor(product)} className="flex w-full items-center gap-4 rounded-xl text-left focus:outline-none focus:ring-2 focus:ring-primary" aria-label={`Uredi proizvod ${product.name}`} data-id="element-257" data-mp="1p6m5ls789f1j">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-gray-50" data-id="element-258" data-mp="ox4a5w12ebsm">
                      {isImageUrl(product.image) ? <img src={product.image} alt="" className="h-full w-full object-cover" data-id="element-259" data-mp="g63yf50xpgij" /> : <span className="flex h-full items-center justify-center text-2xl" data-id="element-260" data-mp="686c4n2mxtsk">
                          {product.image}
                        </span>}
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-261" data-mp="3tdnin6zp6hi1">
                      <h3 className="truncate font-bold text-gray-900" data-id="element-262" data-mp="3odj1deqytiqr">
                        {product.name}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-263" data-mp="1rk7seb7iv4nl">
                        {product.basePrice} €
                      </p>
                    </div>
                    <span className="shrink-0 cursor-grab text-gray-400 active:cursor-grabbing" title="Povucite za promjenu redoslijeda" aria-label="Povucite za promjenu redoslijeda" data-id="element-264" data-mp="1mkbki0vpet2r">
                      <GripVerticalIcon size={21} data-id="element-265" data-mp="1wrxrsg5abo5g" />
                    </span>
                    <EditIcon className="shrink-0 text-primary" size={21} data-id="element-266" data-mp="21pzagzystlge" />
                  </button>
                  <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3" data-id="element-267" data-mp="2pg3e37bzll7e">
                    <button type="button" onClick={() => openProductEditor(product)} className="flex-1 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white" data-id="element-268" data-mp="2tf2f4gulkxtk">
                      Uredi proizvod
                    </button>
                    <IconButton label={product.isActive === false ? 'Aktiviraj' : 'Deaktiviraj'} onClick={() => void runAction(() => updateProduct(product.id, {
                ...product,
                isActive: product.isActive === false
              }), product.isActive === false ? 'Proizvod je aktiviran.' : 'Proizvod je deaktiviran.')} data-id="element-269" data-mp="1404aeldhowmo">
                      {product.isActive === false ? <EyeOffIcon size={17} data-id="element-270" data-mp="rjv6nehhxjmc" /> : <EyeIcon size={17} data-id="element-271" data-mp="mrl9pehyrjak" />}
                    </IconButton>
                    <IconButton label="Obriši" danger onClick={() => {
                if (window.confirm('Obrisati proizvod?')) void runAction(() => deleteProduct(product.id), 'Proizvod je obrisan.');
              }} data-id="element-272" data-mp="3v7eq7tcaourm">
                      <Trash2Icon size={17} data-id="element-273" data-mp="hnkifgqxkcad" />
                    </IconButton>
                  </div>
                </article>)}
            </div>
          </section>}
        {tab === 'gallery' && <section data-id="element-274" data-mp="qge7g5w6iq1f">
            <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-275" data-mp="vfqw8w6m276n">
              <div data-id="element-276" data-mp="vhtx6b9axkq5">
                <h2 className="text-3xl font-black text-gray-900" data-id="element-277" data-mp="10eu1agmlcdem">Galerija</h2>
                <p className="mt-1 text-sm text-gray-600" data-id="element-278" data-mp="10cr153rq53w6">
                  Javno vidljive slike, dostupne svim posjetiteljima.
                </p>
              </div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-primary px-4 py-3 font-bold text-white" data-id="element-279" data-mp="327i8f12cvkzo">
                <ImageIcon size={18} data-id="element-280" data-mp="377e50f8q4a57" />
                Dodaj slike
                <input type="file" accept="image/*" multiple className="hidden" onChange={event => {
              const files = Array.from(event.target.files || []);
              if (files.length) void runAction(async () => {
                for (const file of files) await uploadRemoteGalleryImage(file);
              }, 'Slike su dodane u galeriju.');
              event.target.value = '';
            }} data-id="element-281" data-mp="1oayrp8uo8axa" />
              </label>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-5" data-id="element-282" data-mp="1t98ofnw1njl0">
              {gallery.map(image => <article key={image.id} className="relative aspect-square overflow-hidden rounded-2xl border border-gray-100 bg-white" data-id="element-283" data-mp="mtg52unks9z8">
                  <img src={image.url} alt={image.caption || 'Galerija'} className="h-full w-full object-cover" data-id="element-284" data-mp="jaqtm77gvixd" />
                  <button type="button" aria-label="Obriši sliku" onClick={() => {
              if (window.confirm('Obrisati sliku iz galerije?')) void runAction(() => deleteRemoteGalleryImage(image), 'Slika je obrisana.');
            }} className="absolute right-2 top-2 rounded-full bg-white p-2 text-red-600 shadow" data-id="element-285" data-mp="aau82egdj06j">
                    <Trash2Icon size={16} data-id="element-286" data-mp="3rrvdwlv8u25j" />
                  </button>
                </article>)}
            </div>
          </section>}
        {tab === 'blocked' && <section data-id="element-287" data-mp="3ipcekbqo27o1">
            <h2 className="text-3xl font-black text-gray-900" data-id="element-288" data-mp="3ipm2zuw2c6rm">
              Blokirani termini
            </h2>
            <div className="mt-6 grid gap-6 lg:grid-cols-3" data-id="element-289" data-mp="2z6ojo2co5c7q">
              <form onSubmit={createBlock} className="space-y-4 rounded-3xl border border-gray-100 bg-white p-6" data-id="element-290" data-mp="2u6kcj2xnhtu6">
                <h3 className="font-bold text-gray-900" data-id="element-291" data-mp="343ot8n8q8x3l">Dodaj blokadu</h3>
                <Field label="Od datuma *" data-id="element-292" data-mp="345rrfuk19wje">
                  <input name="date" type="date" required className="input" data-id="element-293" data-mp="3i2oedkx16tv0" />
                </Field>
                <Field label="Do datuma" data-id="element-294" data-mp="3d5522qjw8r5p">
                  <input name="endDate" type="date" className="input" data-id="element-295" data-mp="3d43fbotimg85" />
                </Field>
                <Field label="Proizvod" data-id="element-296" data-mp="3rshosilft2xo">
                  <select name="productId" className="input" data-id="element-297" data-mp="3mug8g5febhpw">
                    <option value="" data-id="element-298" data-mp="3muy8ze93ci3m">Svi proizvodi</option>
                    {products.map(product => <option key={product.id} value={product.id} data-id="element-299" data-mp="4m78eou4d9lu">
                        {product.name}
                      </option>)}
                  </select>
                </Field>
                <Field label="Razlog" data-id="element-300" data-mp="174ad0rivabx6">
                  <input name="reason" className="input" data-id="element-301" data-mp="1bzxzgz5ysqyz" />
                </Field>
                <button type="submit" disabled={isSaving} className="w-full rounded-xl bg-gray-900 py-3 font-bold text-white disabled:cursor-not-allowed disabled:opacity-60" data-id="element-302" data-mp="1gzkdvbxj4d9d">
                  {isSaving ? 'Spremamo blokadu…' : 'Blokiraj termin'}
                </button>
                {blockStatus && <p role="status" className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-800" data-id="element-303" data-mp="xc4j3c921kfc">
                    {blockStatus}
                  </p>}
                {blockError && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm leading-6 text-red-800" data-id="element-304" data-mp="9mikapwlh77v">
                    {blockError}
                  </p>}
              </form>
              <div className="overflow-x-auto rounded-3xl border border-gray-100 bg-white lg:col-span-2" data-id="element-305" data-mp="3t77ro8zvbmtx">
                <table className="w-full text-left" data-id="element-306" data-mp="3p9thae0gb90c">
                  <thead className="bg-gray-50 text-sm text-gray-600" data-id="element-307" data-mp="3k7kpvm388f0c">
                    <tr data-id="element-308" data-mp="3f99h7eoo4ajp">
                      <th className="p-4" data-id="element-309" data-mp="3fakss0ocumld">Datum</th>
                      <th className="p-4" data-id="element-310" data-mp="3fbdv8pga6pxp">Proizvod</th>
                      <th className="p-4" data-id="element-311" data-mp="3ai152avyeb2u">Razlog</th>
                      <th className="p-4" data-id="element-312" data-mp="35jpws4lb48mq" />
                    </tr>
                  </thead>
                  <tbody data-id="element-313" data-mp="31i4n0s42kagr">
                    {blocked.map(block => <tr key={block.id} className="border-t border-gray-100" data-id="element-314" data-mp="31jz7y6fcc2ku">
                        <td className="p-4" data-id="element-315" data-mp="2whqfrcaal4js">
                          {block.date}
                          {block.endDate ? ` – ${block.endDate}` : ''}
                        </td>
                        <td className="p-4" data-id="element-316" data-mp="20h464orm5c66">
                          {products.find(product => product.id === block.productId)?.name || 'Svi proizvodi'}
                        </td>
                        <td className="p-4 text-gray-600" data-id="element-317" data-mp="2ae8rwnhjr5mg">
                          {block.reason || '–'}
                        </td>
                        <td className="p-4 text-right" data-id="element-318" data-mp="1hr4mihejkpa5">
                          <IconButton label="Ukloni blokadu" danger onClick={() => void runAction(() => deleteBlockedDate(block.id), 'Blokada je uklonjena.')} data-id="element-319" data-mp="1hpl4omd8m669">
                            <Trash2Icon size={17} data-id="element-320" data-mp="u2cym27zit36" />
                          </IconButton>
                        </td>
                      </tr>)}
                    {blocked.length === 0 && <tr data-id="element-321" data-mp="3agoteht5vnbu">
                        <td colSpan={4} className="p-10 text-center text-gray-500" data-id="element-322" data-mp="3agx4c45q4ike">
                          Nema blokiranih termina.
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </section>}
        {tab === 'settings' && <section className="max-w-4xl" data-id="element-323" data-mp="3tcn9g08ydv2i">
            <h2 className="text-3xl font-black text-gray-900" data-id="element-324" data-mp="3fg1ccrgwwa95">Postavke</h2>
            <form onSubmit={saveAllSettings} className="mt-6 space-y-6 rounded-3xl border border-gray-100 bg-white p-6" data-id="element-325" data-mp="3ke2una6ik5jk">
              <Field label="Tekst uvjeta najma" data-id="element-326" data-mp="23yrvmcp63wbj">
                <textarea value={settings.termsText} onChange={event => setSettings({
              ...settings,
              termsText: event.target.value
            })} rows={8} className="input" data-id="element-327" data-mp="28zpdeo69moci" />
              </Field>
              <div className="grid gap-4 md:grid-cols-3" data-id="element-328" data-mp="rj2ccryxxtf">
                <Field label="Ime" data-id="element-329" data-mp="r19lq4im1q9">
                  <input value={settings.contactInfo.name} onChange={event => setSettings({
                ...settings,
                contactInfo: {
                  ...settings.contactInfo,
                  name: event.target.value
                }
              })} className="input" data-id="element-330" data-mp="3rxu98pjntsso" />
                </Field>
                <Field label="Telefon" data-id="element-331" data-mp="1g5ypjk4ziulk">
                  <input value={settings.contactInfo.phone} onChange={event => setSettings({
                ...settings,
                contactInfo: {
                  ...settings.contactInfo,
                  phone: event.target.value
                }
              })} className="input" data-id="element-332" data-mp="1c6g7cw4jsxmf" />
                </Field>
                <Field label="Email" data-id="element-333" data-mp="2b60bem2ixonh">
                  <input type="email" value={settings.contactInfo.email} onChange={event => setSettings({
                ...settings,
                contactInfo: {
                  ...settings.contactInfo,
                  email: event.target.value
                }
              })} className="input" data-id="element-334" data-mp="2b71n8srd6f69" />
                </Field>
              </div>
              <details data-id="element-335" data-mp="2xlkqb0pzepi">
                <summary className="cursor-pointer font-bold text-gray-800" data-id="element-336" data-mp="2ywpoe5wisi7">
                  EmailJS obavijesti o novim zahtjevima
                </summary>
                <p className="mt-2 text-sm leading-6 text-gray-600" data-id="element-337" data-mp="byjsn4ea46dd">
                  Nakon svakog novog zahtjeva sustav šalje email na adresu ispod.
                  Klijentov email nije potreban za ovu obavijest.
                </p>
                <div className="mt-4 grid gap-4 md:grid-cols-2" data-id="element-338" data-mp="lvxzqdebbit0">
                  <Field label="EmailJS Public Key" data-id="element-339" data-mp="gxmllqgzim4z">
                    <input placeholder="npr. 9xA1bC2dE3fG4hI5" value={settings.emailJs?.publicKey || ''} onChange={event => setSettings({
                  ...settings,
                  emailJs: {
                    ...settings.emailJs!,
                    publicKey: event.target.value
                  }
                })} className="input" data-id="element-340" data-mp="gx3g598itq7u" />
                  </Field>
                  <Field label="EmailJS Service ID" data-id="element-341" data-mp="1o7xy1r6ctgaw">
                    <input placeholder="npr. service_abc123" value={settings.emailJs?.serviceId || ''} onChange={event => setSettings({
                  ...settings,
                  emailJs: {
                    ...settings.emailJs!,
                    serviceId: event.target.value
                  }
                })} className="input" data-id="element-342" data-mp="1o6mspmx9g0ho" />
                  </Field>
                  <Field label="EmailJS Template ID" data-id="element-343" data-mp="vklfds3bdsmv">
                    <input placeholder="npr. template_abc123" value={settings.emailJs?.templateId || ''} onChange={event => setSettings({
                  ...settings,
                  emailJs: {
                    ...settings.emailJs!,
                    templateId: event.target.value
                  }
                })} className="input" data-id="element-344" data-mp="hkal0f2spvwy" />
                  </Field>
                  <Field label="Email za obavijesti" data-id="element-345" data-mp="377f6hbv3286v">
                    <input type="email" placeholder="vas@email.com" value={settings.emailJs?.notificationEmail || ''} onChange={event => setSettings({
                  ...settings,
                  emailJs: {
                    ...settings.emailJs!,
                    notificationEmail: event.target.value
                  }
                })} className="input" data-id="element-346" data-mp="376vv6dzxui1t" />
                  </Field>
                </div>
              </details>
              <button disabled={isSaving} className="rounded-xl bg-gray-900 px-6 py-3 font-bold text-white disabled:opacity-60" data-id="element-347" data-mp="2augmtf4ly20">
                Spremi postavke
              </button>
            </form>
            <section className="mt-6 rounded-3xl border border-gray-100 bg-white p-6" data-id="element-348" data-mp="b3xu31pruawn">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-349" data-mp="g3ac995p6tsw">
                Sigurnosna kopija
              </h3>
              <p className="mt-2 text-sm text-gray-600" data-id="element-350" data-mp="pzdr9flnemx9">
                Izvoz preuzima aktualne podatke iz Supabasea. Uvoz je namjerno
                onemogućen kako se produkcijski podaci ne bi slučajno prepisali.
              </p>
              <button type="button" onClick={() => void runAction(downloadBackup, 'Backup je preuzet.')} className="mt-4 rounded-xl border border-gray-200 px-5 py-3 font-bold text-gray-800" data-id="element-351" data-mp="2rriggjbrj64a">
                Izvezi Supabase podatke
              </button>
            </section>
            <section className="mt-6 rounded-3xl border border-primary/20 bg-primary/5 p-6" data-id="element-352" data-mp="2lo5foa8ibh0n">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-353" data-mp="2hms5xp2amtog">
                Prijenos postojećih podataka
              </h3>
              {migration ? <>
                  <p className="mt-2 text-sm text-gray-700" data-id="element-354" data-mp="1tzbs1rfrz1h1">
                    Supabase: {migration.remoteCounts.products} proizvoda,{' '}
                    {migration.remoteCounts.bookings} rezervacija,{' '}
                    {migration.remoteCounts.blockedDates} blokada.{' '}
                    {migration.remoteIsEmpty ? 'Baza je prazna.' : 'Baza već sadrži podatke.'}
                  </p>
                  {migration.remoteCounts.products === 0 ? <button type="button" disabled={isSaving} onClick={() => void migrate()} className="mt-4 rounded-xl bg-primary px-5 py-3 font-bold text-white disabled:opacity-60" data-id="element-355" data-mp="39gc3um42whoz">
                      Prenesi lokalne podatke jednom
                    </button> : <p className="mt-4 text-sm font-bold text-gray-600" data-id="element-356" data-mp="3cxqdvge7sd2w">
                      Migracija je zaključana kako bi se spriječili duplikati.
                    </p>}
                </> : <p className="mt-2 text-sm text-gray-600" data-id="element-357" data-mp="3qwgdb2oiaouf">
                  Provjera stanja migracije nije dostupna.
                </p>}
            </section>
          </section>}
      </main>
      {productModalOpen && <ProductModal product={editingProduct} images={productImages} isUploading={isUploadingImages} onClose={() => {
      setProductModalOpen(false);
      setEditingProduct(null);
    }} onImagesSelected={selectProductImages} onRemoveImage={index => setProductImages(items => items.filter((_, itemIndex) => itemIndex !== index))} onSubmit={saveProduct} data-id="element-358" data-mp="ndn3n0yumk5j" />}
    </div>;
}
function ProductModal({
  product,
  images,
  isUploading,
  onClose,
  onImagesSelected,
  onRemoveImage,
  onSubmit
}: {
  product: Product | null;
  images: string[];
  isUploading: boolean;
  onClose: () => void;
  onImagesSelected: (files: FileList | null) => Promise<void>;
  onRemoveImage: (index: number) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
}) {
  return <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4" data-id="element-359" data-mp="2tjicp4x5u7gk">
      <div className="mx-auto my-8 max-w-3xl rounded-3xl bg-white shadow-xl" data-id="element-360" data-mp="2thfc5qydsvxl">
        <form onSubmit={event => void onSubmit(event)} className="p-6" data-id="element-361" data-mp="2ybrx65fiafbg">
          <div className="mb-5 flex items-center justify-between" data-id="element-362" data-mp="37o8v10z83wuh">
            <h2 className="text-2xl font-black" data-id="element-363" data-mp="33nf6q6hvcg12">
              {product ? 'Uredi proizvod' : 'Novi proizvod'}
            </h2>
            <button type="button" onClick={onClose} aria-label="Zatvori" data-id="element-364" data-mp="3hf3k0d3utajm">
              <XIcon data-id="element-365" data-mp="3cg0obj9rkgob" />
            </button>
          </div>
          <div className="grid gap-4 md:grid-cols-2" data-id="element-366" data-mp="3cgad53j2ohsf">
            <Field label="Naziv *" data-id="element-367" data-mp="2opmupobj69rm">
              <input name="name" required defaultValue={product?.name} className="input" data-id="element-368" data-mp="2oqogonu34go4" />
            </Field>
            <Field label="Kategorija *" data-id="element-369" data-mp="2yp5cby3yxjgi">
              <select name="category" defaultValue={product?.category || 'Napuhanci'} className="input" data-id="element-370" data-mp="2ylznmjz94jlb">
                <option data-id="element-371" data-mp="dwk0q035lf0y">Napuhanci</option>
                <option data-id="element-372" data-mp="8z0jcrb5sm4s">Soft Play</option>
                <option data-id="element-373" data-mp="8y8pbkx7mwgm">Sport</option>
                <option data-id="element-374" data-mp="91efl3hks4dx">Dodaci</option>
              </select>
            </Field>
            <Field label="Opis" data-id="element-375" data-mp="gzgd5c6it4ug">
              <textarea name="description" defaultValue={product?.description} className="input" data-id="element-376" data-mp="l1bcib4w585l" />
            </Field>
            <Field label="Uvjeti korištenja" data-id="element-377" data-mp="pybhsjtx26vn">
              <textarea name="usageTerms" defaultValue={product?.usageTerms} rows={3} className="input" data-id="element-378" data-mp="uzr7ip71h1kd" />
            </Field>
            <Field label="Dimenzije" data-id="element-379" data-mp="2a209u3qk5ab">
              <input name="dimensions" defaultValue={product?.dimensions} className="input" data-id="element-380" data-mp="2b3icij65s1j" />
            </Field>
            <Field label="Dobno ograničenje" data-id="element-381" data-mp="c1eu2r93h5iz">
              <input name="ageRestriction" defaultValue={product?.ageRestriction} className="input" data-id="element-382" data-mp="3aqn5czt8ye85" />
            </Field>
            <Field label="Kapacitet" data-id="element-383" data-mp="3fnn9h56jd6wm">
              <input name="capacity" defaultValue={product?.capacity} className="input" data-id="element-384" data-mp="386qrefpoguxa" />
            </Field>
            <Field label="Primjena *" data-id="element-385" data-mp="3359pcgvgj2f4">
              <select name="application" defaultValue={product?.application || 'Unutarnji i vanjski prostor'} className="input" data-id="element-386" data-mp="2y6zwwe14afx7">
                <option data-id="element-387" data-mp="2p8nxbmi9w7sm">Unutarnji i vanjski prostor</option>
                <option data-id="element-388" data-mp="2kgcxejnj8m02">Unutarnji prostor</option>
                <option data-id="element-389" data-mp="2kejoexpg5pow">Vanjski prostor</option>
              </select>
            </Field>
            <Field label="Osnovna cijena (€) *" data-id="element-390" data-mp="2fhtb2i511876">
              <input name="basePrice" type="number" min="0" step="0.01" required defaultValue={product?.basePrice} className="input" data-id="element-391" data-mp="2aiytfjisppon" />
            </Field>
            <Field label="URL ili emoji glavne slike" data-id="element-392" data-mp="2q8enyfdqr5hy">
              <input name="image" defaultValue={images[0] || product?.image} className="input" data-id="element-393" data-mp="2q8y904youa07" />
            </Field>
            <div className="md:col-span-2" data-id="element-394" data-mp="2hf1oeul695mw">
              <Field label="Varijante (JSON, opcionalno)" data-id="element-395" data-mp="2he0bsloicb32">
                <textarea name="variants" defaultValue={product?.variants ? JSON.stringify(product.variants, null, 2) : ''} rows={3} className="input font-mono text-xs" data-id="element-396" data-mp="2lgmyk86bn5te" />
              </Field>
            </div>
            <div className="md:col-span-2" data-id="element-397" data-mp="22iap0lbhztxa">
              <p className="mb-2 text-sm font-bold text-gray-700" data-id="element-398" data-mp="22j3vrmagijff">
                Slike proizvoda
              </p>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-gray-900 px-4 py-3 font-bold text-white" data-id="element-399" data-mp="3stf8rpgpv2y6">
                <ImageIcon size={18} data-id="element-400" data-mp="1l8zwmephx306" />
                {isUploading ? 'Prijenos…' : 'Prenesi slike'}
                <input type="file" accept="image/*" multiple disabled={isUploading} className="hidden" onChange={event => {
                void onImagesSelected(event.target.files);
                event.target.value = '';
              }} data-id="element-401" data-mp="1h8pigerjv6a8" />
              </label>
              <div className="mt-4 grid grid-cols-4 gap-3" data-id="element-402" data-mp="1zz79mewqhtth">
                {images.map((image, index) => <div key={`${image}-${index}`} className="relative aspect-square overflow-hidden rounded-xl border border-gray-200" data-id="element-403" data-mp="2dx49bk6higcd" data-mpi={index}>
                    {isImageUrl(image) ? <img src={image} alt="" className="h-full w-full object-cover" data-id="element-404" data-mp="jm8onji0zat8" /> : <span className="flex h-full items-center justify-center text-2xl" data-id="element-405" data-mp="2epngm7ethst4">
                        {image}
                      </span>}
                    <button type="button" onClick={() => onRemoveImage(index)} className="absolute right-1 top-1 rounded-full bg-white p-1 text-red-600" aria-label="Ukloni sliku" data-id="element-406" data-mp="2ojxc3xz23782">
                      <XIcon size={14} data-id="element-407" data-mp="32jykrzlkbiy3" />
                    </button>
                  </div>)}
              </div>
            </div>
          </div>
          <div className="mt-6 flex gap-3" data-id="element-408" data-mp="3cat713yt6ijj">
            <button className="flex-1 rounded-xl bg-gray-900 py-3 font-bold text-white" data-id="element-409" data-mp="3cajjdn18knh0">
              Spremi proizvod
            </button>
            <button type="button" onClick={onClose} className="rounded-xl bg-gray-100 px-5 py-3 font-bold text-gray-700" data-id="element-410" data-mp="3lbr5z7ue3zv9">
              Odustani
            </button>
          </div>
        </form>
      </div>
    </div>;
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <label className="block text-sm font-bold text-gray-700" data-id="element-411" data-mp="10ptv0hvmr0vp">
      <span className="mb-1 block" data-id="element-412" data-mp="vpy0d98tolst">{label}</span>
      {children}
    </label>;
}
function Stat({
  label,
  value
}: {
  label: string;
  value: string | number;
}) {
  return <article className="rounded-2xl border border-gray-100 bg-white p-5" data-id="element-413" data-mp="1ocs6t28t1kna">
      <p className="text-sm font-bold text-gray-500" data-id="element-414" data-mp="1ofo7gyc1enff">{label}</p>
      <p className="mt-2 text-3xl font-black text-gray-900" data-id="element-415" data-mp="1jl0ofezsyl88">{value}</p>
    </article>;
}
function IconButton({
  label,
  children,
  onClick,
  danger = false
}: {
  label: string;
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return <button type="button" title={label} aria-label={label} onClick={onClick} className={`rounded-lg p-2 ${danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-416" data-mp="27bwcc1150wa7">
      {children}
    </button>;
}
function Status({
  status
}: {
  status: Booking['status'];
}) {
  const labels = {
    pending: 'Na čekanju',
    confirmed: 'Potvrđeno',
    rejected: 'Odbijeno',
    cancelled: 'Otkazano'
  };
  const styles = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-700'
  };
  return <span className={`rounded-full px-2 py-1 text-xs font-bold ${styles[status]}`} data-id="element-417" data-mp="23bteh4fifxv1">
      {labels[status]}
    </span>;
}
function parseVariants(value: string): ProductVariant[] | undefined | null {
  if (!value.trim()) return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed) || !parsed.every(item => typeof item === 'object' && item !== null && typeof (item as ProductVariant).label === 'string' && typeof (item as ProductVariant).price === 'number')) return null;
    return parsed as ProductVariant[];
  } catch {
    return null;
  }
}
function optionalText(value: FormDataEntryValue | null): string | undefined {
  const text = String(value || '').trim();
  return text || undefined;
}
function createId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function isImageUrl(value: string): boolean {
  return value.startsWith('http') || value.startsWith('data:');
}